import moment from 'moment';
import { PureComponent, ReactNode } from 'react';
import { connect } from 'react-redux';
import getAbhaMetricsDashboard, { dhis_states } from '../api/getAbhaMetricsDashboard';
import NavBar from '../components/common/NavBar';
import NewHeaderComponent from '../components/common/NewHeaderComponent';
import { GlobalState } from '../store/reducers';
import { getGenieSetting, getMonthlyAbhaLoss } from '../store/selectors/session';
import { track } from '../tracking';
import { captureError } from './captureError';
import { Helmet } from 'react-helmet';
import ABHADashboardV2 from '../components/ABHADashboard/ABHADashboardV2';
import CurrMonthLossStrip from '../components/common/CurrMonthLossStrip';
import { AbhaDashBoardState } from '../components/abha/types';
import { AbhaFilled } from '../components/common/Icons';
import { AntTooltip } from '../components/common/AntTooltip';
import ClinicSelector from '../components/common/ClinicSelector';
import {
    getDoctorSelectorList,
    getMyClinics,
    getSelectedClinicId,
    setSelectedClinicId,
} from '../store/idbHandler';

export type FetchState = 'loading' | 'success' | 'error';
export type ABHADashboardStatusFilterType = 'total-incentive' | 'potential-incentive-loss';
export type ABHADashboardStatusLabelType = 'Total incentive' | 'Potential incentive loss';

interface Props {
    isABDM?: boolean;
    currMonthLoss?: number;
}

export type FilterData = {
    [k in ABHADashboardStatusFilterType]: { count: number; amount?: number };
};

class ABHADashboard extends PureComponent<Props, AbhaDashBoardState> {
    counts: FilterData | undefined = undefined;

    state: AbhaDashBoardState = {
        monthlyAchieved: [],
        switchColumn: 'success',
        tableKey: 0,
        doctorProfile: undefined,
        totalIncentive: 0,
        currClinic: getSelectedClinicId(),
    };

    componentDidMount = (): void => {
        const hfr = getMyClinics()?.find(
            (c) => c._id === (this.state.currClinic ?? getSelectedClinicId()),
        )?.abdm?.hfr_id;

        if (!!hfr) {
            this.fetchTransactions();
        }
    };

    componentDidUpdate(
        prevProps: Readonly<Props>,
        prevState: Readonly<AbhaDashBoardState>,
        snapshot?: any,
    ): void {
        if (prevState.currClinic !== this.state.currClinic) {
            const hfr = getMyClinics()?.find(
                (c) => c._id === (this.state.currClinic ?? getSelectedClinicId()),
            )?.abdm?.hfr_id;

            if (!!hfr) this.fetchTransactions();
        }
    }

    fetchTransactions = (): void => {
        this.setState({
            fetchState: 'loading',
        });

        getAbhaMetricsDashboard(getDoctorSelectorList()?.[0]?.id || '', this.state.currClinic ?? '')
            .then((res) => {
                const response = res.response;

                if (res.error) {
                    throw new Error(res.error);
                }

                if (response) {
                    const monthlyAchieved = response[0].monthly_achieved || [];
                    const lastUpdatedAt = response[0].last_updated_at || '';
                    this.setState({
                        fetchState: 'success',
                        monthlyAchieved,
                        lastUpdated: lastUpdatedAt,
                        tableKey: this.state.tableKey + 1,
                        dhisStatus: res.dhisStatus,
                        dhisIssues: res.dhisIssues,
                    });
                }
            })
            .catch((e) => {
                this.counts = undefined;
                this.setState({
                    fetchState: 'error',
                });
                track('ERROR', { on: 'getAbhaMetricsDashboard', what: (e as Error).message });
                captureError(e);
            });
    };

    setDhisStatus = (val?: dhis_states) => this.setState({ dhisStatus: val });

    render(): ReactNode {
        const { lastUpdated, dhisStatus, fetchState, dhisIssues } = this.state;
        const isABDM =
            this.props.isABDM &&
            !!getMyClinics()?.find(
                (c) => c._id === (this.state.currClinic ?? getSelectedClinicId()),
            )?.abdm?.hfr_id;

        return (
            <div className="grid h-full overflow-x-auto overflow-y-hidden grid-cols-queueGrid min-w-840">
                <Helmet>
                    <meta charSet="utf-8" />
                    <title>EKA - ABHA Dashboard</title>
                </Helmet>

                <NavBar />
                <div className="flex flex-col h-full col-span-11 overflow-hidden">
                    <NewHeaderComponent
                        title="ABHA Dashboard"
                        filterComponent={
                            <div className="flex w-full justify-between items-stretch gap-8">
                                <div className="flex gap-8">
                                    {lastUpdated && (
                                        <AntTooltip
                                            content={`Last updated at ${moment(lastUpdated).format(
                                                'LLLL',
                                            )}`}
                                            placement="bottom"
                                        >
                                            <div className="flex relative top-2 items-center gap-4">
                                                <p className="text-neutral-5 sm:text-ellipsis-style md:overflow-visible text-13">
                                                    Last updated at{' '}
                                                    {moment(lastUpdated).format(
                                                        "ddd, DD MMM'YY, hA",
                                                    )}
                                                </p>
                                            </div>
                                        </AntTooltip>
                                    )}
                                    <ClinicSelector
                                        onClinicSelect={(selectedClinicId) => {
                                            setSelectedClinicId(selectedClinicId);
                                            this.setState({ currClinic: selectedClinicId });
                                        }}
                                        currClinicId={this.state.currClinic}
                                    />
                                </div>
                                <div className="flex gap-8 ml-auto">
                                    {isABDM && (
                                        <div
                                            className="p-8 self-stretch no-underline text-14 rounded-8 flex items-center self-start justify-center cursor-pointer border border-darwin-accent-bright-blue-800 text-darwin-accent-bright-blue-800 bg-white font-700"
                                            onClick={() => window.open('/claim-abha.pdf', '_blank')}
                                            style={{ marginLeft: 'auto' }}
                                        >
                                            How to claim incentive
                                        </div>
                                    )}
                                </div>
                            </div>
                        }
                    />
                    {isABDM ? (
                        this.props.currMonthLoss && this.props.currMonthLoss <= 0 ? null : (
                            <CurrMonthLossStrip
                                currMonthLoss={this.props.currMonthLoss}
                                className="justify-center gap-4"
                                textClassName="text-14"
                                icon={AbhaFilled('w-20 h-20')}
                            />
                        )
                    ) : null}

                    {isABDM ? (
                        <div
                            className={`flex-1 py-16 flex space-x-16 h-full overflow-hidden ${
                                this.props.currMonthLoss && this.props.currMonthLoss <= 0
                                    ? 'pt-0'
                                    : ''
                            }`}
                        >
                            <div className="flex-1 flex flex-col overflow-auto p-16">
                                <ABHADashboardV2
                                    abhaMetricData={this.state}
                                    defaultTab={
                                        this.state?.monthlyAchieved
                                            ? this.state?.monthlyAchieved[0]?.month
                                            : undefined
                                    }
                                    currMonthLoss={this.props.currMonthLoss}
                                    currClinic={this.state.currClinic ?? getSelectedClinicId()}
                                    dhisStatus={dhisStatus}
                                    dhisIssues={dhisIssues}
                                    setDhisStatus={this.setDhisStatus}
                                    fetchState={fetchState}
                                />
                            </div>
                        </div>
                    ) : (
                        <div className="flex-1 flex space-x-16 h-full overflow-hidden">
                            <div className="flex-1 flex items-center justify-center overflow-hidden h-full">
                                <iframe
                                    className="w-full h-full"
                                    src="https://product.eka.care/dhis"
                                />
                            </div>
                        </div>
                    )}
                </div>
            </div>
        );
    }
}
export default connect((state: GlobalState) => ({
    isABDM: getGenieSetting(state, 'abdm') as boolean,
    currMonthLoss: getMonthlyAbhaLoss(state),
}))(ABHADashboard);
